import React, { useEffect, useState, useCallback } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Motorcycle, Timer, CheckCircle, X, SignOut, Lightning,
  MapPin, Phone, ArrowRight, Receipt, Star, Hourglass,
} from "@phosphor-icons/react";
import LocationGate from "../../lib/LocationGate";
import { gigApi, gigFormatErr, GIG_TOKEN_KEY, GIG_PARTNER_KEY } from "../../lib/gigApi";

export default function PartnerAppShell() {
  const nav = useNavigate();
  const [me, setMe] = useState(null);
  const [tab, setTab] = useState("duty");  // duty · offers · jobs · earnings
  const [tick, setTick] = useState(0);

  const refresh = useCallback(async () => {
    try {
      const r = await gigApi.get("/gig-partner-app/me");
      setMe(r.data);
      localStorage.setItem(GIG_PARTNER_KEY, JSON.stringify(r.data));
    } catch (e) {
      if (e.response?.status === 401) {
        nav("/partner-app/login", { replace: true });
      }
    }
  }, [nav]);

  useEffect(() => {
    if (!localStorage.getItem(GIG_TOKEN_KEY)) {
      nav("/partner-app/login", { replace: true }); return;
    }
    refresh();
    const i = setInterval(() => setTick(t => t + 1), 15000);
    return () => clearInterval(i);
  // eslint-disable-next-line
  }, []);
  useEffect(() => { if (tick) refresh(); // eslint-disable-next-line
  }, [tick]);

  const logout = () => {
    localStorage.removeItem(GIG_TOKEN_KEY);
    localStorage.removeItem(GIG_PARTNER_KEY);
    nav("/partner-app/login", { replace: true });
  };

  if (!localStorage.getItem(GIG_TOKEN_KEY)) {
    return <Navigate to="/partner-app/login" replace/>;
  }
  if (!me) return <div className="min-h-screen bg-[#0F1F0C] text-white/60 flex items-center justify-center">Loading…</div>;

  return (
    <LocationGate
      pingPath="/api/gig-partner-app/location/ping"
      tokenKey={GIG_TOKEN_KEY}
      bodyShape="customer"
      label="Rescigo Partner"
      onRevoked={() => logout()}
    >
      <div className="min-h-screen bg-[#0F1F0C] text-white" data-testid="partner-app-shell">
        <header className="sticky top-0 z-20 bg-[#0F1F0C] border-b border-white/10 px-5 py-3 flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-[#C7E0C2] text-[#0F1F0C] flex items-center justify-center">
            <Motorcycle size={18} weight="bold"/>
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-[0.2em] text-white/50 leading-none">Partner</div>
            <div className="font-heading text-base truncate" data-testid="partner-name">{me.name}</div>
          </div>
          <button onClick={logout} data-testid="partner-logout"
                   className="w-9 h-9 rounded-md bg-white/5 flex items-center justify-center hover:bg-white/10">
            <SignOut size={16}/>
          </button>
        </header>

        <main className="pb-24 px-4 pt-4 max-w-2xl mx-auto">
          {tab === "duty"     && <DutyTab me={me} onChange={refresh}/>}
          {tab === "offers"   && <OffersTab onChange={refresh}/>}
          {tab === "jobs"     && <JobsTab/>}
          {tab === "earnings" && <EarningsTab/>}
        </main>

        <nav className="fixed inset-x-0 bottom-0 z-20 bg-[#0F1F0C] border-t border-white/10 flex">
          {[
            { k: "duty",     label: "Duty",     icon: Lightning },
            { k: "offers",   label: "Offers",   icon: Timer },
            { k: "jobs",     label: "Jobs",     icon: MapPin },
            { k: "earnings", label: "Earnings", icon: Receipt },
          ].map(({ k, label, icon: Ico }) => (
            <button key={k} onClick={() => setTab(k)}
                     data-testid={`partner-tab-${k}`}
                     className={`flex-1 py-3 text-[10px] uppercase tracking-[0.18em] flex flex-col items-center gap-0.5 ${
                       tab === k ? "text-[#C7E0C2]" : "text-white/50"
                     }`}>
              <Ico size={18} weight={tab === k ? "fill" : "duotone"}/>
              <span>{label}</span>
            </button>
          ))}
        </nav>
      </div>
    </LocationGate>
  );
}

// ----------------------------------------------------------
function DutyTab({ me, onChange }) {
  const [picks, setPicks] = useState(me.active_drop_locations || []);
  const [vehicle, setVehicle] = useState(me.active_vehicle || me.vehicle_type || "bike");
  const [busy, setBusy] = useState(false);
  const onDuty = me.duty_status === "on";

  const toggle = async () => {
    setBusy(true);
    try {
      if (onDuty) {
        await gigApi.post("/gig-partner-app/duty/off");
        toast.success("Signed off duty");
      } else {
        if (!picks.length) { toast.error("Pick at least one drop location"); setBusy(false); return; }
        await gigApi.post("/gig-partner-app/duty/on",
                            { active_drop_locations: picks,
                               active_vehicle: vehicle });
        toast.success("You're on duty");
      }
      await onChange();
    } catch (e) { toast.error(gigFormatErr(e)); }
    setBusy(false);
  };

  return (
    <div className="space-y-4" data-testid="partner-duty">
      <div className="rounded-2xl bg-white/5 border border-white/10 p-5">
        <div className="text-[11px] uppercase tracking-wider text-white/50">Status</div>
        <div className={`text-3xl font-heading mt-1 ${onDuty ? "text-[#C7E0C2]" : "text-white/80"}`}>
          {onDuty ? "ON DUTY" : "OFF DUTY"}
        </div>
        <div className="text-xs text-white/50 mt-1">
          Rating {me.rating_avg ? `${me.rating_avg.toFixed(1)} ★` : "—"}
          · {me.orders_lifetime || 0} orders lifetime
        </div>
      </div>

      <div className="rounded-2xl bg-white/5 border border-white/10 p-5 space-y-3">
        <div className="text-[11px] uppercase tracking-wider text-white/50">Active drop location(s)</div>
        <div className="flex flex-wrap gap-2">
          {(me.assigned_drop_locations || []).length === 0 && (
            <div className="text-xs text-white/50">
              No drop locations assigned yet. Ask admin to configure yours.
            </div>
          )}
          {(me.assigned_drop_locations || []).map(d => (
            <button key={d}
                     onClick={() => setPicks(p => p.includes(d) ? p.filter(x => x !== d) : [...p, d])}
                     data-testid={`partner-drop-${d}`}
                     className={`h-9 px-3 rounded-full text-xs border transition ${
                       picks.includes(d)
                         ? "bg-[#C7E0C2] text-[#0F1F0C] border-[#C7E0C2]"
                         : "border-white/20 text-white/80"}`}>
              {d}
            </button>
          ))}
        </div>
        <div>
          <label className="block text-[11px] uppercase tracking-wider text-white/50 mb-1">Vehicle</label>
          <select value={vehicle} onChange={e => setVehicle(e.target.value)}
                   data-testid="partner-vehicle"
                   className="h-10 w-full px-2 rounded-md bg-white/10 border border-white/10 text-sm text-white">
            {["bike", "scooter", "auto", "van", "car", "none"].map(v =>
              <option key={v} value={v} className="text-black">{v}</option>)}
          </select>
        </div>
      </div>

      <button onClick={toggle} disabled={busy} data-testid="partner-duty-toggle"
               className={`h-14 w-full rounded-2xl text-base font-semibold ${
                 onDuty ? "bg-[#F4C7B9] text-[#7A2E18]" : "bg-[#C7E0C2] text-[#0F1F0C]"
               } disabled:opacity-50`}>
        {busy ? "Working…" : onDuty ? "Go off duty" : "Go on duty"}
      </button>
    </div>
  );
}

// ----------------------------------------------------------
function OffersTab({ onChange }) {
  const [offers, setOffers] = useState([]);
  const [busy, setBusy] = useState(null);
  const [decline, setDecline] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await gigApi.get("/gig-partner-app/offers");
      setOffers(r.data || []);
    } catch (e) {}
  }, []);
  useEffect(() => {
    load();
    const i = setInterval(load, 5000);
    return () => clearInterval(i);
  }, [load]);

  const accept = async (o) => {
    setBusy(o.id);
    try {
      const r = await gigApi.post("/gig-partner-app/offers/accept", { offer_id: o.id });
      toast.success("Offer accepted! Head to the customer.");
      await load();
      onChange?.();
    } catch (e) { toast.error(gigFormatErr(e)); }
    setBusy(null);
  };

  const doDecline = async (reason) => {
    const o = decline;
    setBusy(o.id);
    try {
      await gigApi.post("/gig-partner-app/offers/decline", { offer_id: o.id, reason });
      toast("Declined");
      setDecline(null);
      await load();
    } catch (e) { toast.error(gigFormatErr(e)); }
    setBusy(null);
  };

  return (
    <div className="space-y-4" data-testid="partner-offers">
      {offers.length === 0 && (
        <div className="rounded-2xl bg-white/5 border border-white/10 p-8 text-center text-sm text-white/50">
          <Hourglass size={32} weight="duotone" className="mx-auto mb-3"/>
          Listening for nearby jobs… keep this screen open.
        </div>
      )}
      {offers.map(o => (
        <div key={o.id} className="rounded-2xl bg-white text-[#0F1F0C] p-5 shadow-xl"
             data-testid={`offer-${o.id}`}>
          {o.priority === "URGENT" && (
            <div className="bg-[#F4C7B9] text-[#7A2E18] text-[10px] uppercase font-bold tracking-wider rounded px-2 py-0.5 inline-block mb-2">
              Urgent
            </div>
          )}
          <div className="text-[11px] uppercase tracking-wider text-[#8A8780]">Service</div>
          <div className="text-lg font-heading">{o.sr?.service_category || "Pickup"}</div>
          <div className="text-xs text-[#5C5A55] mt-1 flex items-center gap-1">
            <MapPin size={12}/> {o.customer?.name || "Customer"} · {o.customer?.lsgd || ""}
          </div>
          <div className="mt-4 flex items-end justify-between">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-[#8A8780]">Earning</div>
              <div className="text-3xl font-heading text-[#2D5A27]">₹{o.earning?.estimated_earning}</div>
            </div>
            <div className="text-right">
              <div className="text-[10px] uppercase tracking-wider text-[#8A8780]">Distance</div>
              <div className="text-lg">{o.distance_km} km</div>
            </div>
          </div>
          <div className="mt-4 flex gap-2">
            <button onClick={() => setDecline(o)} disabled={busy === o.id}
                     data-testid={`decline-${o.id}`}
                     className="flex-1 h-12 rounded-md bg-[#F4C7B9] text-[#7A2E18] text-sm font-semibold">
              Decline
            </button>
            <button onClick={() => accept(o)} disabled={busy === o.id}
                     data-testid={`accept-${o.id}`}
                     className="flex-[2] h-12 rounded-md bg-[#2D5A27] text-white text-sm font-semibold">
              {busy === o.id ? "…" : "Accept"}
            </button>
          </div>
        </div>
      ))}

      {decline && (
        <div className="fixed inset-0 z-30 bg-black/60 flex items-end" data-testid="decline-sheet">
          <div className="w-full bg-white text-[#0F1F0C] rounded-t-3xl p-6 space-y-2">
            <div className="font-heading text-lg">Why declining?</div>
            {["traffic", "break", "out_of_area", "unsafe", "other"].map(r => (
              <button key={r} onClick={() => doDecline(r)}
                       data-testid={`decline-reason-${r}`}
                       className="w-full h-11 rounded-md border border-[#E5E2D9] text-left px-3 text-sm">
                {r.replace(/_/g, " ")}
              </button>
            ))}
            <button onClick={() => setDecline(null)}
                     data-testid="decline-cancel"
                     className="w-full h-10 text-xs text-[#8A8780]">cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ----------------------------------------------------------
function JobsTab() {
  const [job, setJob] = useState(null);
  const [otp, setOtp] = useState("");
  const [bill, setBill] = useState("");
  const [busy, setBusy] = useState(false);
  const [stars, setStars] = useState(0);

  const load = async () => {
    // Find the SR currently accepted by me (via /me then client-side).
    try {
      // No dedicated endpoint for brevity — reuse /offers which no longer
      // shows accepted; we fetch /me to find any active job via SR.
      // Simplest: query the backend's SR by partner via a general call.
      const r = await gigApi.get("/gig-partner-app/me");
      // Shadow: the main agent fetches the most recent accepted dispatch.
      // For Phase 1, we don't yet have a dedicated endpoint; show a hint.
      setJob(null);  // placeholder — admin surface handles live view
    } catch (e) {}
  };
  useEffect(() => { load(); }, []);

  // Stub screen: Phase 1 uses the admin surface for the full timeline.
  // The partner's active job UI will reuse the same arrival-OTP flow
  // once the back-end /jobs/active endpoint lands.
  return (
    <div className="space-y-4" data-testid="partner-jobs">
      <div className="rounded-2xl bg-white/5 border border-white/10 p-6 text-sm text-white/60">
        <div className="font-heading text-base text-white mb-2">Active job</div>
        After accepting an offer, open the SR link your admin shares and
        enter the customer's 4-digit arrival OTP to start the job.
        {/* Phase 2: this tab will render the full map, OTP entry and
            Complete flow using /gig-partner-app/jobs/* endpoints. */}
      </div>
    </div>
  );
}

// ----------------------------------------------------------
function EarningsTab() {
  const [scope, setScope] = useState("today");
  const [data, setData] = useState(null);
  useEffect(() => {
    gigApi.get("/gig-partner-app/earnings", { params: { scope } })
      .then(r => setData(r.data)).catch(() => setData(null));
  }, [scope]);

  return (
    <div className="space-y-4" data-testid="partner-earnings">
      <div className="flex gap-2">
        {["today", "week", "month", "all"].map(k => (
          <button key={k} onClick={() => setScope(k)}
                   data-testid={`earnings-scope-${k}`}
                   className={`h-8 px-3 rounded-full text-xs ${
                     scope === k ? "bg-[#C7E0C2] text-[#0F1F0C]" : "bg-white/10"
                   }`}>
            {k}
          </button>
        ))}
      </div>
      {data && (
        <>
          <div className="rounded-2xl bg-white/5 border border-white/10 p-5">
            <div className="text-[11px] uppercase tracking-wider text-white/50">Total · {scope}</div>
            <div className="text-4xl font-heading text-[#C7E0C2] mt-1">₹{data.total.toFixed(2)}</div>
            <div className="text-xs text-white/60 mt-2 grid grid-cols-3 gap-3">
              <div>Pending ₹{data.pending.toFixed(2)}</div>
              <div>Payable ₹{data.payable.toFixed(2)}</div>
              <div>Paid ₹{data.paid.toFixed(2)}</div>
            </div>
          </div>
          <div className="space-y-2">
            {(data.rows || []).map(r => (
              <div key={r.id} className="rounded-xl bg-white/5 border border-white/10 p-3 text-sm"
                   data-testid={`earning-${r.id}`}>
                <div className="flex items-center justify-between">
                  <div className="text-white/80">{r.sr_id?.slice(0, 8)}</div>
                  <div className="font-semibold text-[#C7E0C2]">₹{r.estimated_earning}</div>
                </div>
                <div className="text-[11px] text-white/50 mt-0.5">
                  {r.created_at?.slice(0, 16)} · {r.distance_km} km · {r.status}
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
